import React, { useEffect, useRef, useState } from "react";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";

import { NewTabLink } from "components/NewTabLink";
import {
  CUSTOMER_AUTOMATED_TRACKING_BLOG_URL,
  EVOTRUX_BLUE,
} from "constants/index";

import { CustomerSelectorHeader, CustomerSelectorInput } from "./components";
import {
  createEmailCustomer,
  CUSTOMER_TYPES,
  getCustomerKey,
  handleCustomerPaste,
  isValidEmail,
} from "./utils";

import type {
  CustomerSelectionItem,
  UnifiedCustomerSelectorProps,
} from "./types";

function UnifiedCustomerSelector({
  selectedItems,
  options,
  inputValue,
  onSelectionChange,
  onInputChange,
  loading,
  error,
  hasNoCustomers,
  isInAddCustomersToShipmentModal = false,
  hideBorder = false,
  autoFocus = false,
}: UnifiedCustomerSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [emailError, setEmailError] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedIds = new Set(
    selectedItems.map((item) => {
      if (item.type === CUSTOMER_TYPES.INTERNAL) {
        return `internal-${item.role_id}`;
      }
      if (item.type === CUSTOMER_TYPES.EXTERNAL) {
        return `external-${item.id}`;
      }
      return `email-${item.email}`;
    }),
  );

  const handleRemove = (item: CustomerSelectionItem) => {
    const newSelections = selectedItems.filter((selected) => {
      if (
        item.type === CUSTOMER_TYPES.INTERNAL &&
        selected.type === CUSTOMER_TYPES.INTERNAL
      ) {
        return selected.role_id !== item.role_id;
      }
      if (
        item.type === CUSTOMER_TYPES.EXTERNAL &&
        selected.type === CUSTOMER_TYPES.EXTERNAL
      ) {
        return selected.id !== item.id;
      }
      if (
        item.type === CUSTOMER_TYPES.EMAIL &&
        selected.type === CUSTOMER_TYPES.EMAIL
      ) {
        return selected.email !== item.email;
      }
      return true;
    });
    onSelectionChange(newSelections);
  };

  const handleClearAll = () => {
    onSelectionChange([]);
    onInputChange("");
  };

  const handleSelectItem = (item: CustomerSelectionItem) => {
    let itemId: string;
    if (item.type === CUSTOMER_TYPES.INTERNAL) {
      itemId = `internal-${item.role_id}`;
    } else if (item.type === CUSTOMER_TYPES.EXTERNAL) {
      itemId = `external-${item.id}`;
    } else {
      itemId = `email-${item.email}`;
    }

    if (selectedIds.has(itemId)) {
      handleRemove(item);
    } else {
      onSelectionChange([...selectedItems, item]);
    }
    onInputChange("");
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      (e.key === "Enter" ||
        e.key === " " ||
        e.key === "Tab" ||
        e.key === "Shift") &&
      inputValue.trim()
    ) {
      e.preventDefault();
      const trimmed = inputValue.trim();
      if (isValidEmail(trimmed)) {
        const normalizedEmail = trimmed.toLowerCase();
        const alreadyAdded = selectedItems.some(
          (s) => s.type === CUSTOMER_TYPES.EMAIL && s.email === normalizedEmail,
        );
        if (!alreadyAdded) {
          const emailItem = createEmailCustomer(trimmed);
          onSelectionChange([...selectedItems, emailItem]);
        }
        onInputChange("");
        setEmailError("");
      } else {
        setEmailError(`Invalid email: ${trimmed}`);
      }
    }
    if (
      e.key === "Backspace" &&
      inputValue === "" &&
      selectedItems.length > 0
    ) {
      const newSelections = selectedItems.slice(0, -1);
      onSelectionChange(newSelections);
    }
  };

  const handleFocus = () => {
    setIsOpen(true);
  };

  const handleContainerClick = () => {
    setIsOpen(true);
    inputRef.current?.focus();
  };

  const handleContainerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      setIsOpen(true);
      inputRef.current?.focus();
    }
  };

  const handleInputChange = (value: string) => {
    onInputChange(value);
    if (emailError) setEmailError("");
  };

  const handlePasteWithErrorHandling = (event: React.ClipboardEvent) => {
    const originalOnInputChange = onInputChange;
    const originalOnSelectionChange = onSelectionChange;

    const pasteProps = {
      currentSelections: selectedItems,
      availableCustomers: options,
      onSelectionChange: originalOnSelectionChange,
      onInputChange: originalOnInputChange,
      onErrorChange: setEmailError,
    };

    handleCustomerPaste(event, pasteProps);
  };

  // Filter dropdown items based on input
  const query = inputValue.toLowerCase();
  const filteredCustomers = options.filter((c) => {
    const searchFields = [
      c.email.toLowerCase(),
      c.type === CUSTOMER_TYPES.INTERNAL
        ? `${c.first_name} ${c.last_name}`.toLowerCase()
        : "",
      c.type === CUSTOMER_TYPES.INTERNAL ? c.company_name.toLowerCase() : "",
      c.type === CUSTOMER_TYPES.EXTERNAL && c.first_name && c.last_name
        ? `${c.first_name} ${c.last_name}`.toLowerCase()
        : "",
      c.type === CUSTOMER_TYPES.EXTERNAL && c.company_name
        ? c.company_name.toLowerCase()
        : "",
    ].filter(Boolean);

    return searchFields.some((field) => field.includes(query));
  });

  const selectorInput = (
    <CustomerSelectorInput
      selectedItems={selectedItems}
      inputValue={inputValue}
      isOpen={isOpen}
      emailError={emailError}
      containerRef={containerRef}
      inputRef={inputRef}
      filteredCustomers={filteredCustomers}
      selectedIds={selectedIds}
      onInputChange={handleInputChange}
      onKeyDown={handleKeyDown}
      onPaste={handlePasteWithErrorHandling}
      onRemoveItem={handleRemove}
      onClearAll={handleClearAll}
      onFocus={handleFocus}
      onContainerClick={handleContainerClick}
      onContainerKeyDown={handleContainerKeyDown}
      onSelectItem={handleSelectItem}
      getCustomerKey={getCustomerKey}
      autoFocus={autoFocus}
    />
  );

  const trackingBlurb = (
    <Typography variant="body2" sx={{ mt: "4px", color: EVOTRUX_BLUE }}>
      • Customers will receive live tracking updates as your shipment moves.{" "}
      <NewTabLink href={CUSTOMER_AUTOMATED_TRACKING_BLOG_URL}>
        Learn more
      </NewTabLink>
    </Typography>
  );

  return (
    <Box sx={hideBorder ? {} : { mb: 3 }}>
      <CustomerSelectorHeader
        error={error}
        hasNoCustomers={hasNoCustomers}
        loading={loading}
        isInAddCustomersToShipmentModal={isInAddCustomersToShipmentModal}
      />
      {hideBorder ? (
        <Box>
          {selectorInput}
          {trackingBlurb}
        </Box>
      ) : (
        <Box
          component="fieldset"
          sx={{
            border: `${isOpen ? 2 : 1}px solid ${EVOTRUX_BLUE}`,
            borderRadius: "4px",
            padding: "0 12px 12px",
            margin: 0,
            minWidth: 0,
          }}
        >
          <Box
            component="legend"
            sx={{
              fontSize: "12px",
              color: EVOTRUX_BLUE,
              px: "4px",
              ml: "8px",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            Notify Customer(s)
            <Tooltip title="Customers will receive live tracking updates as your shipment moves.">
              <IconButton
                size="small"
                sx={{ p: 0, color: EVOTRUX_BLUE, fontSize: "12px" }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
          {selectorInput}
          {trackingBlurb}
        </Box>
      )}
    </Box>
  );
}

export { UnifiedCustomerSelector };
